import { cn } from '@/lib/utils';
import { DatePicker } from './date-picker';

interface DateTimePickerProps {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

const selectClass =
  'flex h-10 w-[4.5rem] rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

/**
 * Date + time picker built from plain, locale-independent parts (the app's
 * own calendar-based DatePicker + Hour/Minute selects) instead of a native
 * <input type="datetime-local"> — that control's placeholder/format follows
 * the browser's OS locale and on some Chromium builds spells out the full
 * month name instead of numeric mm, showing garbled dashes.
 */
export function DateTimePicker({ value, onChange, placeholder, minDate, maxDate, className }: DateTimePickerProps) {
  const hour = value?.getHours();
  const minute = value?.getMinutes();

  const setDatePart = (d: Date | undefined) => {
    if (!d) { onChange(undefined); return; }
    const next = new Date(d);
    next.setHours(hour ?? 0, minute ?? 0, 0, 0);
    onChange(next);
  };
  const setHour = (h: number) => {
    const base = value ? new Date(value) : new Date();
    base.setHours(h, base.getMinutes() || 0, 0, 0);
    onChange(base);
  };
  const setMinute = (m: number) => {
    const base = value ? new Date(value) : new Date();
    base.setMinutes(m, 0, 0);
    onChange(base);
  };

  return (
    <div className={cn('flex gap-2', className)}>
      <DatePicker value={value} onChange={setDatePart} placeholder={placeholder} minDate={minDate} maxDate={maxDate} className="flex-1" />
      <select
        value={hour ?? ''}
        onChange={(e) => setHour(Number(e.target.value))}
        disabled={!value}
        className={selectClass}
        aria-label="Hour"
      >
        <option value="" disabled>HH</option>
        {Array.from({ length: 24 }, (_, h) => (
          <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
        ))}
      </select>
      <select
        value={minute ?? ''}
        onChange={(e) => setMinute(Number(e.target.value))}
        disabled={!value}
        className={selectClass}
        aria-label="Minute"
      >
        <option value="" disabled>MM</option>
        {[0, 15, 30, 45].map((m) => (
          <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
        ))}
      </select>
    </div>
  );
}
